import { Camera, Users, Handshake, Calendar, DollarSign, BarChart3, User, Lightbulb, PenTool, Send, TrendingUp, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMG } from "@/lib/site";

const SERVICES = [
  { icon: Camera, t: "Content Production", d: "High-quality photo and video content tailored to your brand." },
  { icon: Users, t: "Creator Management", d: "Strategy, coaching, and growth to build your brand." },
  { icon: Handshake, t: "Brand Partnerships", d: "We connect you with brands and paid collaborations." },
  { icon: Calendar, t: "Merchandise & Print Products", d: "Calendars, posters, prints, and more." },
  { icon: DollarSign, t: "Monetization Strategy", d: "Diversify income and maximize earnings." },
  { icon: BarChart3, t: "Performance Tracking", d: "Detailed analytics to measure growth and results." },
];

const FLOW = [
  { icon: User, t: "Consultation", d: "We learn about your goals." },
  { icon: Lightbulb, t: "Strategy", d: "We create your content plan." },
  { icon: Camera, t: "Shoot", d: "Professional photo & video content." },
  { icon: PenTool, t: "Create", d: "We edit and craft content that performs." },
  { icon: Send, t: "Publish", d: "We post and launch to grow your brand." },
  { icon: TrendingUp, t: "Grow", d: "We optimize and keep scaling your brand." },
];

const WHY = ["Creator-First Approach", "Professional Team", "Proven Strategies", "Long-Term Partnership"];

export default function Services() {
  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 text-center" data-testid="services-hero">
        <Reveal>
          <p className="overline text-gold mb-4">What We Offer</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase text-white">Our Services</h1>
          <p className="mt-5 text-white/60 text-lg max-w-2xl mx-auto">Everything you need to build, grow, and monetize your brand.</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 0.07}>
              <div className="group border border-white/10 p-9 h-full hover:border-gold/50 hover:-translate-y-1 transition-[transform,border-color] duration-300 bg-[#0b0b0b]" data-testid={`service-${i}`}>
                <s.icon size={38} strokeWidth={1.2} className="text-gold mb-6" />
                <h3 className="font-cond uppercase tracking-wide text-xl text-white mb-3">{s.t}</h3>
                <p className="text-white/55 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["We don't just create content", "We build businesses"]} />

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="workflow">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="overflow-hidden mb-8">
              <img src={IMG.servicesPortrait} alt="" className="w-full h-80 object-cover" />
            </div>
            <p className="overline text-gold mb-4">Why Fan Focus?</p>
            <p className="text-white/70 mb-6 leading-relaxed">We're more than a production company. We're your partner in building a profitable, lasting brand.</p>
            <ul className="space-y-3">
              {WHY.map((w, i) => (
                <li key={i} className="flex items-center gap-3 text-white/85"><Check size={18} className="text-gold" /> {w}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl md:text-5xl uppercase text-white mb-10">Our Workflow</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {FLOW.map((f, i) => (
                <Reveal key={i} delay={(i % 3) * 0.06} className="bg-[#0a0a0a]">
                  <div className="p-7 h-full" data-testid={`flow-${i}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-display text-3xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                      <f.icon size={22} strokeWidth={1.3} className="text-white/50" />
                    </div>
                    <h3 className="font-cond uppercase tracking-wide text-white mb-2">{f.t}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{f.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
